import { Component } from '@theme/component';

const STORAGE_KEY = 'hanro_gender';

class HanroGenderNav extends Component {
  /** @type {Map<string, string>} */
  #landingPages = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#landingPages.set('womens', this.dataset.womensUrl || '/pages/womens');
    this.#landingPages.set('mens', this.dataset.mensUrl || '/pages/mens');
    this.#landingPages.set('world-of-hanro', this.dataset.secondaryUrl || '/pages/world-of-hanro');

    this.#initFromStorage();
    this.addEventListener('click', this.#handleClick);

    // Horizon's hydration morph replaces tab buttons with fresh server HTML
    // (no .is-active), wiping our state ~1s after page load. Watch for
    // childList changes (node replacement) and re-apply once, then disconnect.
    // We only observe childList (not attributes) so our own classList/setAttribute
    // calls don't trigger the observer.
    this.#observeHydration();

    // For mobile drawer: re-apply state each time the drawer opens
    const details = this.closest('details');
    if (details) {
      details.addEventListener('toggle', this.#handleDrawerToggle);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    const details = this.closest('details');
    if (details) {
      details.removeEventListener('toggle', this.#handleDrawerToggle);
    }
  }

  #initFromStorage() {
    const stored = this.#getGender();
    const isHomepage =
      window.location.pathname === '/' || document.body.classList.contains('template-index');
    const fallback = this.dataset.fallbackGender || 'womens';

    if (stored) {
      // Show stored gender's menu; only highlight tab if NOT on homepage
      this.#activateGender(stored, { navigate: false, highlightTab: !isHomepage });
    } else if (fallback !== 'none') {
      // No stored gender: show fallback menu without highlighting tab
      this.#activateGender(fallback, { navigate: false, highlightTab: false });
    }
  }

  /** @returns {string|null} */
  #getGender() {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** @param {string} gender */
  #setGender(gender) {
    try {
      sessionStorage.setItem(STORAGE_KEY, gender);
    } catch {
      // Silent — navigation still works, just won't persist
    }
  }

  /**
   * @param {string} gender
   * @param {{ navigate?: boolean, highlightTab?: boolean }} options
   */
  #activateGender(gender, { navigate = false, highlightTab = true } = {}) {
    // Update tab states across all instances (desktop + mobile)
    document.querySelectorAll('[data-gender-tab]').forEach((tab) => {
      const active = highlightTab && tab.dataset.genderTab === gender;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });

    this.#toggleMenuVisibility(gender);

    if (navigate) {
      this.#setGender(gender);
      const url = this.#landingPages.get(gender);
      if (url) window.location.href = url;
    }
  }

  /** @param {string} gender */
  #toggleMenuVisibility(gender) {
    document.querySelectorAll('[data-gender-menu]').forEach((menu) => {
      menu.hidden = menu.dataset.genderMenu !== gender;
    });
  }

  #observeHydration() {
    const observer = new MutationObserver(() => {
      observer.disconnect();
      // Use microtask so we re-apply after the morph batch completes
      queueMicrotask(() => this.#initFromStorage());
    });
    observer.observe(this, { childList: true, subtree: true });
  }

  /** Re-apply gender state when the drawer opens */
  #handleDrawerToggle = () => {
    this.#initFromStorage();
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-gender-tab]');
    if (!tab) return;

    const gender = tab.dataset.genderTab;
    const shouldNavigate = this.dataset.navigate !== 'false';

    this.#setGender(gender);

    if (shouldNavigate) {
      // Navigate immediately — don't toggle menus first, it causes a flash
      // when the new page loads with server-rendered default then switches
      const url = this.#landingPages.get(gender);
      if (url) window.location.href = url;
    } else {
      // Mobile drawer: switch menus in-place
      this.#activateGender(gender, { navigate: false, highlightTab: true });
    }
  };
}

if (!customElements.get('hanro-gender-nav')) {
  customElements.define('hanro-gender-nav', HanroGenderNav);
}
